"use client";

import { Link } from "@/i18n/routing";
import { IconForwardArrow, IconMenuLinkArrow, IconSmallForwardArrow } from "@repo/assets/images";
import { HamburgerButton, OffCanvasMenu } from "@repo/ui";
import cx from "classnames";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
const CategoryMenuOCP = ({
  menu
}) => {
  const t = useTranslations("Common");
  const tAccount = useTranslations("Account");
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenus, setActiveMenus] = useState([menu]);
  const [activeLevels, setActiveLevels] = useState([true]); // activeLevels = [true, true ...]
  const [breadcrumb, setBreadcrumb] = useState([{
    title: t("Categories"),
    url: "",
    isRoot: true
  }]);
  const hasSubcategories = useCallback(item => {
    return item?.subcategories && Array.isArray(item.subcategories) && item.subcategories.length > 0;
  }, []);
  const isActiveItem = useCallback((item, level) => {
    return breadcrumb[level + 1]?.title === item.name;
  }, [breadcrumb]);
  const handleMenuAction = useCallback((item, action, level) => {
    switch (action) {
      case "navigate":
        {
          // Push subcategories to activeMenus
          const newActiveMenus = [...activeMenus.slice(0, level + 1), item.subcategories];
          const newBreadcrumb = [...breadcrumb.slice(0, level + 1), {
            title: item.name,
            url: `/${item.slug}`
          }];
          const isDrawerOpen = activeLevels[level + 1] === true;
          if (!isDrawerOpen) {
            // First time opening slide in
            const newActiveLevels = [...activeLevels.slice(0, level + 1), false];
            setActiveMenus(newActiveMenus);
            setBreadcrumb(newBreadcrumb);
            setActiveLevels(newActiveLevels);

            // Slide in new drawer
            requestAnimationFrame(() => {
              setActiveLevels(prev => {
                const updated = [...prev];
                updated[level + 1] = true;
                return updated;
              });
            });
          } else {
            // Drawer already open replace content
            const newActiveLevels = [...activeLevels.slice(0, level + 1), true];
            setActiveMenus(newActiveMenus);
            setBreadcrumb(newBreadcrumb);
            setActiveLevels(newActiveLevels);
          }
          break;
        }
      case "select":
        {
          router.push(`/collections/${item.slug}`);
          setIsOpen(false);
          break;
        }
      case "back":
        {
          // Slide out current drawer
          setActiveLevels(prev => {
            const updated = [...prev];
            updated[level] = false;
            return updated;
          });

          // Clean up after animation
          setTimeout(() => {
            setActiveMenus(prev => prev.slice(0, level));
            setBreadcrumb(prev => prev.slice(0, level));
            setActiveLevels(prev => prev.slice(0, level));
          }, 300);
          break;
        }
      default:
        break;
    }
  }, [activeMenus, breadcrumb, activeLevels, router]);
  const handleToggleMenu = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      setTimeout(() => {
        // Reset to initial state
        setActiveMenus([menu]);
        setBreadcrumb([{
          title: t("Categories"),
          url: "",
          isRoot: true
        }]);
        setActiveLevels([true]);
      }, 300);
    } else {
      setIsOpen(true);
    }
  }, [isOpen, menu, t]);
  const handleCloseMenu = useCallback(index => {
    if (typeof index === "number") {
      handleMenuAction(null, "back", index);
    } else {
      handleToggleMenu();
    }
  }, [handleMenuAction, handleToggleMenu]);
  if (menu?.length === 0 || !menu) return null;
  return <>
      <button onClick={handleToggleMenu} className="hidden items-center gap-3 md:flex">
        <span>{t("Shop_By")}</span>
        <IconSmallForwardArrow className="text-white rtl:-rotate-180" data-sentry-element="IconSmallForwardArrow" data-sentry-source-file="CategoryMenuOCP.jsx" data-sentry-element="IconSmallForwardArrow" />
      </button>

      <HamburgerButton open={isOpen} handleOpenMenu={handleToggleMenu} showOnMd={false} data-sentry-element="HamburgerButton" data-sentry-source-file="CategoryMenuOCP.jsx" data-sentry-element="HamburgerButton" />

      <div className="flex">
        {activeMenus.map((currentMenu, level) => {
        const handleItemClick = item => handleMenuAction(item, hasSubcategories(item) ? "navigate" : "select", level);
        return <MenuLevel key={level} menu={currentMenu} level={level} isVisible={isOpen && activeLevels[level]} breadcrumbItem={breadcrumb[level]} totalLevels={activeMenus.length} hasSubcategories={hasSubcategories} isActiveItem={isActiveItem} onItemClick={handleItemClick} onClose={handleCloseMenu} onToggleMenu={handleToggleMenu} t={t} tAccount={tAccount} />;
      })}
      </div>
    </>;
};
const MenuLevel = ({
  menu,
  level,
  isVisible,
  breadcrumbItem,
  totalLevels,
  hasSubcategories,
  isActiveItem,
  onItemClick,
  onClose,
  onToggleMenu,
  tAccount
}) => {
  const isRoot = breadcrumbItem?.isRoot === true;
  const DynamicTag = isRoot ? "div" : Link;
  return <OffCanvasMenu key={level} open={isVisible} handleClose={onClose} title={breadcrumbItem?.title} position="left" index={level} totalMenus={totalLevels} data-sentry-element="OffCanvasMenu" data-sentry-component="MenuLevel" data-sentry-source-file="CategoryMenuOCP.jsx">
      <div className="border-supportiveDarkGray flex flex-col">
        <DynamicTag {...!isRoot && {
        href: `/collections${breadcrumbItem?.url}`
      }} className={cx("border-supportiveDarkGray font-heading text-supportiveBlack rtl:font-arabic group h-full border-b text-base font-semibold md:text-xl", {
        "px-4 py-2.5": isRoot,
        "hover:text-lightYellow px-4 py-[19.5px]": !isRoot
      })} {...!isRoot && {
        onClick: onToggleMenu
      }} data-sentry-element="DynamicTag" data-sentry-source-file="CategoryMenuOCP.jsx">
          <div className="flex items-center justify-between">
            <span>{breadcrumbItem?.title}</span>
            {!isRoot && <span className="font-body text-lightYellow rtl:font-arabic text-sm font-semibold">
                {tAccount("See_All")}
              </span>}
          </div>
        </DynamicTag>

        <ul>
          {menu?.map(item => {
          const hasNextLevel = hasSubcategories(item);
          const isActive = isActiveItem(item, level);
          const DynamicTag = hasNextLevel ? "div" : Link;
          return <li key={`${item.name}-${item.id}-${level}`} className={cx("border-supportiveDarkGray text-supportiveBlack group border-b px-4 text-sm font-medium md:text-lg", {
            "cursor-pointer hover:bg-[#F5F2EE]": hasNextLevel,
            "hover:text-lightYellow": !hasNextLevel,
            "bg-lightYellow": isActive
          })}>
                <DynamicTag {...!hasNextLevel && {
              href: `/collections/${item.slug}`
            }} className="block py-2.5" onClick={hasNextLevel ? () => onItemClick(item) : onToggleMenu}>
                  <div className="flex items-center justify-between">
                    {item?.name && <span>{item.name}</span>}
                    {hasNextLevel ? <IconForwardArrow className="text-supportiveBlack transition-transform group-hover:ltr:translate-x-1 rtl:rotate-180 group-hover:rtl:-translate-x-1" /> : <IconMenuLinkArrow className="text-lightYellow me-2 -rotate-45 transition-transform group-hover:-translate-y-1 ltr:group-hover:translate-x-1 rtl:rotate-[-135deg] rtl:group-hover:-translate-x-1" />}
                  </div>
                </DynamicTag>
              </li>;
        })}
        </ul>
      </div>
    </OffCanvasMenu>;
};
export default CategoryMenuOCP;